import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BoltIcon, PlusIcon } from "@heroicons/react/24/outline";

import useSystemStepFields from "../../data/useSystemStepFields";
import { ISystemStepField } from "../../models/SystemStepField";
import useNotification from "../notifications/useNotifications";

// Shared by the check-in, triage and depollution step editors and their tick-off screens. A "system
// step" is mapped to a vehicle/lead field, so its answer is written into that field when it's ticked
// off. Field labels, descriptions and answers come from the API in English and are translated here
// for display only - the saved answer stays the English option so the API can parse it.

export function useSystemStepField(key?: string | null) {
  const { systemStepFields } = useSystemStepFields();
  const fields = systemStepFields.data?.data ?? [];
  return {
    fields,
    field: key ? fields.find((x) => x.key.toLowerCase() === key.toLowerCase()) : undefined,
    isLoading: systemStepFields.isLoading,
  };
}

// The options string a mapped step is saved with - the same comma-separated shape the editor uses.
export const systemStepOptionsString = (field: ISystemStepField) =>
  field.options.length > 0 ? field.options.join(",") : null;

// The API refuses an answer a system field can't take with a 400 that says why ("Mileage must be a
// whole number of 0 or more"), so the tick-off screens show that rather than a bare "failed to save".
// useApiHelper puts that message on the Error; anything else is just "HTTP Error: 500" and not worth showing.
export const stepSaveErrorReason = (error?: unknown) =>
  error instanceof Error && !error.message.startsWith("HTTP Error") ? error.message : undefined;

export function useStepSaveErrorHandler(objectName: string) {
  const { t } = useTranslation("common");
  const { addNotification } = useNotification();

  return (error?: unknown) => {
    addNotification({
      variant: "error",
      primaryText: t("Failed to save {{name}}", { name: objectName.toLowerCase() }),
      secondaryText: stepSaveErrorReason(error),
    });
  };
}

// Answers to a system step are English canonical values; show them translated.
export function useSystemStepOptionLabel() {
  const { t } = useTranslation("vehicles");
  return (step: { systemField?: string | null }, option: string) => (step.systemField ? t(option) : option);
}

function AnswerSummary({ field }: { field: ISystemStepField }) {
  const { t } = useTranslation("vehicles");

  if (field.options.length > 0) {
    return <>{t("Answers: {{options}}", { options: field.options.map((x) => t(x)).join(", ") })}</>;
  }

  return <>{field.input === "Number" ? t("Answer: a whole number") : t("Answer: free text")}</>;
}

export function SystemStepFieldPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (field: ISystemStepField | null) => void;
}) {
  const { t } = useTranslation("vehicles");
  const { fields, field, isLoading } = useSystemStepField(value);
  const [picking, setPicking] = useState(false);

  if (value) {
    return (
      <div className="rounded-md border border-primary-200 bg-primary-50 p-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-medium text-primary-800">
              <BoltIcon className="h-4 w-4 flex-shrink-0" />
              {field
                ? t("Updates {{target}} → {{field}}", { target: t(field.target), field: t(field.label) })
                : isLoading
                  ? t("Loading...")
                  : t("Mapped to an unknown field ({{key}})", { key: value })}
            </p>
            {field && (
              <>
                <p className="mt-1 text-xs text-primary-700">{t(field.description)}</p>
                <p className="mt-1 text-xs text-gray-600"><AnswerSummary field={field} /></p>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="flex-shrink-0 text-xs font-medium text-primary-700 underline hover:text-primary-900"
          >
            {t("Remove mapping")}
          </button>
        </div>
      </div>
    );
  }

  if (!picking) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-primary-300 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 shadow-sm hover:bg-primary-50"
        >
          <PlusIcon className="h-4 w-4" />
          {t("Add System Step")}
        </button>
        <span className="text-xs text-gray-500">{t("Map the answer onto vehicle data, e.g. Engine starts → Vehicle Starting.")}</span>
      </div>
    );
  }

  const groups: { target: ISystemStepField["target"]; label: string }[] = [
    { target: "Vehicle", label: t("Vehicle") },
    { target: "Lead", label: t("Lead") },
  ];

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-gray-900" htmlFor="system-step-field">{t("System field")}</label>
      <div className="flex items-center gap-3">
        <select
          id="system-step-field"
          autoFocus
          defaultValue=""
          disabled={isLoading}
          onChange={(e) => {
            const picked = fields.find((x) => x.key === e.target.value);
            if (!picked) return;
            setPicking(false);
            onChange(picked);
          }}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="" disabled>{isLoading ? t("Loading...") : t("Choose the field this step updates...")}</option>
          {groups.map((group) => (
            <optgroup key={group.target} label={group.label}>
              {fields.filter((x) => x.target === group.target).map((x) => (
                <option key={x.key} value={x.key}>{t(x.label)}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <button type="button" onClick={() => setPicking(false)} className="text-xs text-gray-500 underline hover:text-gray-700">
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
}

// Small pill for the step tables, so an admin can see which steps write to vehicle data.
export function SystemStepFieldBadge({ systemField }: { systemField?: string | null }) {
  const { t } = useTranslation("vehicles");
  const { field } = useSystemStepField(systemField);

  if (!systemField) return null;

  return (
    <span
      title={field ? t("Updates {{target}} → {{field}}", { target: t(field.target), field: t(field.label) }) : systemField}
      className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
    >
      <BoltIcon className="h-3 w-3" />
      {field ? t(field.label) : t("System")}
    </span>
  );
}

// Under a step on the tick-off screens: tells the operator the answer updates the vehicle and shows
// what the vehicle holds now (e.g. what the customer said at quote), without pre-selecting it.
export function SystemStepCurrentValue({
  systemField,
  values,
}: {
  systemField?: string | null;
  values?: Record<string, string>;
}) {
  const { t } = useTranslation("vehicles");
  const { field } = useSystemStepField(systemField);

  if (!systemField) return null;

  const current = values?.[systemField];

  return (
    <div className="mt-0.5 flex items-center gap-1 text-xs text-primary-700" title={field ? t(field.description) : undefined}>
      <BoltIcon className="h-3 w-3 flex-shrink-0" />
      <span>
        {field?.target === "Lead" ? t("Updates lead") : t("Updates vehicle")}
        {current !== undefined && (
          <span className="text-gray-500"> · {t("Currently: {{value}}", { value: t(current) })}</span>
        )}
      </span>
    </div>
  );
}
